import React from 'react';
import { cn } from '../../../lib/utils';

type Tone = 'primary' | 'success' | 'warning' | 'destructive' | 'purple';

export interface TaskyProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  value: number; // 0-100
  tone?: Tone;
  size?: 'sm' | 'md';
  label?: React.ReactNode;
  showValue?: boolean;
  /** バー（role=progressbar）のアクセシブルな名前。同じ画面にバーが複数あるときに何の進み具合かを伝える */
  barLabel?: string;
}

const toneClass: Record<Tone, string> = {
  primary: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  destructive: 'bg-destructive',
  purple: 'bg-purple',
};

export const TaskyProgress: React.FC<TaskyProgressProps> = ({
  className,
  value,
  tone = 'primary',
  size = 'sm',
  label,
  showValue,
  barLabel,
  ...rest
}) => {
  const v = Math.min(100, Math.max(0, value));
  return (
    <div {...rest} className={cn('w-full', className)}>
      {(label || showValue) && (
        <div className="flex items-center justify-between mb-1">
          {label && <span className="text-sm font-medium text-dark">{label}</span>}
          {showValue && <span className="text-xs text-gray-500">{Math.round(v)}%</span>}
        </div>
      )}
      <div
        role="progressbar"
        aria-label={barLabel}
        aria-valuenow={v}
        aria-valuemin={0}
        aria-valuemax={100}
        className={cn(
          'w-full bg-gray-200 rounded-full overflow-hidden',
          size === 'sm' && 'h-2',
          size === 'md' && 'h-3',
        )}
      >
        <div
          className={cn('h-full rounded-full transition-[width]', toneClass[tone])}
          style={{ width: `${v}%` }}
        />
      </div>
    </div>
  );
};
